import {
  createApi,
  createApiUtils,
  createCustomServiceCall,
  GetInferredFromRaw,
} from '@thinknimble/tn-models'
import { axiosInstance } from '../axios-instance'

import {
  createReceiptShape,
  receiptShape,
  receiptFilterShape,
  Receipt,
  CreateReceipt,
} from './models'
import { z } from 'zod'
import { mapWithConcurrency } from '../../utils/concurrency'
import { RECEIPT_UPLOAD_CONCURRENCY } from './upload-limits'

// Re-export so existing importers keep resolving the cap from the api module.
export { RECEIPT_UPLOAD_CONCURRENCY } from './upload-limits'

const RECEIPT_UPLOAD_URI = '/expenses/receipts/'

// Reuse tn-models' camelCasing so a standalone upload returns the same shape as
// the generated API. At runtime createApiUtils nests the helpers under `.utils`
// (the published typings flatten them), so reach through with a narrow cast.
const { fromApi: receiptFromApi } = (
  createApiUtils({
    name: 'uploadReceiptFile',
    outputShape: receiptShape,
    disableLoggingWarning: true,
  }) as unknown as { utils: { fromApi: (data: object) => Receipt } }
).utils

export interface UploadReceiptOptions {
  /** Called with 0→100 as the request body streams up. */
  onUploadProgress?: (percent: number) => void
  signal?: AbortSignal
}

/**
 * Upload a single receipt as its own multipart request. Progress is reported
 * purely from the browser's own upload-progress events — no backend job,
 * websocket, or polling. The returned promise resolves with the created
 * Receipt or rejects with the request error (the caller decides retry).
 */
export const uploadReceiptFile = async (
  item: CreateReceipt,
  { onUploadProgress, signal }: UploadReceiptOptions = {},
): Promise<Receipt> => {
  const data = new FormData()
  data.append('name', item.name)
  data.append('asset', item.asset)
  if (item.reimburseTo) data.append('reimburse_to', item.reimburseTo)

  const response = await axiosInstance.post(RECEIPT_UPLOAD_URI, data, {
    headers: { 'Content-Type': 'multipart/form-data' },
    signal,
    onUploadProgress: (event) => {
      if (!onUploadProgress || !event.total) return
      onUploadProgress(Math.round((event.loaded / event.total) * 100))
    },
  })
  return receiptFromApi(response.data)
}

const bulkCreate = createCustomServiceCall({
  inputShape: {
    items: z.object(createReceiptShape).array(),
  },
  outputShape: {
    succeeded: z.object(receiptShape).array(),
    failed: z.object({ name: z.string() }).array(),
  },

  cb: async ({ input }) => {
    // Each file is its own request against the existing per-file endpoint,
    // fanned out with a bounded concurrency cap. Using settled semantics means
    // one file's failure never aborts or blocks the others.
    const settled = await mapWithConcurrency(input.items, RECEIPT_UPLOAD_CONCURRENCY, (item) =>
      uploadReceiptFile(item),
    )

    return {
      succeeded: settled.flatMap((res) => (res.status === 'fulfilled' ? [res.value] : [])),
      failed: input.items.flatMap((item, i) =>
        settled[i].status === 'rejected' ? [{ name: item.name }] : [],
      ),
    }
  },
})

export const aiGeneratedExpenseShape = {
  name: z.string(),
  amount: z.number(),
  amountCurrency: z.string(),
  notes: z.string().optional(),
  expenseDate: z.string(),
  cashFlowType: z.string(),
  category: z.string(),
  expenseCategory: z.string().nullable().optional(),
  providedId: z.string().optional(),
  receipts: z.array(z.string()).nullable().optional(),
  organizationName: z.string().optional(),
  organization: z.string().nullable().optional(),
  paymentAccountHint: z.string().optional(),
  paymentAccount: z.string().nullable().optional(),
  reports: z.array(z.string()).nullable().optional(),
  // Sent back on expense create to tie the correction diff to this generation.
  extractionId: z.string().nullable().optional(),
}

export type AiGeneratedExpense = GetInferredFromRaw<typeof aiGeneratedExpenseShape>

const generateCustom = createCustomServiceCall({
  inputShape: {
    receiptId: z.string(),
    // Which org's wallet pays. Optional: when omitted the backend bills the
    // receipt's own org. The receipt-list flow sends it explicitly so the user
    // picks a funded org; the in-form AI-fill path omits it.
    organization: z.string().uuid().optional(),
    context: z.string().optional(),
  },
  outputShape: {
    asyncJobId: z.string().uuid(),
  },

  cb: async ({ client, slashEndingBaseUri, input, utils }) => {
    const url = `${slashEndingBaseUri}${input.receiptId}/generate/` as `${string}/`
    const body: Record<string, string> = {}
    if (input.organization) body.organization = input.organization
    if (input.context) body.context = input.context
    const response = await client.post(url, body)
    return utils.fromApi(response.data)
  },
})

const duplicate = createCustomServiceCall({
  inputShape: {
    receiptId: z.string(),
  },
  outputShape: receiptShape,

  cb: async ({ client, slashEndingBaseUri, input, utils }) => {
    const url = `${slashEndingBaseUri}${input.receiptId}/duplicate/` as `${string}/`
    const response = await client.post(url)
    return utils.fromApi(response.data)
  },
})

const linkReceipt = createCustomServiceCall({
  inputShape: {
    receiptId: z.string(),
    targetReceiptId: z.string(),
  },
  outputShape: receiptShape,

  cb: async ({ client, slashEndingBaseUri, input, utils }) => {
    const url = `${slashEndingBaseUri}${input.receiptId}/link/` as `${string}/`
    const response = await client.post(url, { receipt_id: input.targetReceiptId })
    return utils.fromApi(response.data)
  },
})

const batchGenerate = createCustomServiceCall({
  inputShape: {
    receiptIds: z.array(z.string().uuid()),
    organization: z.string().uuid(),
  },
  outputShape: {
    jobs: z.array(
      z.object({
        receiptId: z.string(),
        asyncJobId: z.string(),
      }),
    ),
    skipped: z.array(z.string()),
  },

  cb: async ({ client, slashEndingBaseUri, input, utils }) => {
    const response = await client.post(`${slashEndingBaseUri}batch-generate/`, {
      receipt_ids: input.receiptIds,
      organization: input.organization,
    })
    return utils.fromApi(response.data)
  },
})

const mergeReceipts = createCustomServiceCall({
  inputShape: {
    receiptIds: z.array(z.string()).min(2),
  },
  outputShape: receiptShape,

  cb: async ({ client, slashEndingBaseUri, input, utils }) => {
    const response = await client.post(`${slashEndingBaseUri}merge/`, {
      receipt_ids: input.receiptIds,
    })
    return utils.fromApi(response.data)
  },
})

const unlinkReceipt = createCustomServiceCall({
  inputShape: {
    receiptId: z.string(),
    targetReceiptId: z.string(),
  },
  outputShape: receiptShape,

  cb: async ({ client, slashEndingBaseUri, input, utils }) => {
    const url = `${slashEndingBaseUri}${input.receiptId}/unlink/` as `${string}/`
    const response = await client.post(url, { receipt_id: input.targetReceiptId })
    return utils.fromApi(response.data)
  },
})

export const receiptApi = createApi({
  client: axiosInstance,
  baseUri: '/expenses/receipts/',
  models: {
    entity: receiptShape,
    create: createReceiptShape,
    extraFilters: {
      ...receiptFilterShape,
    },
  },
  customCalls: {
    bulkCreate,
    generateCustom,
    batchGenerate,
    duplicate,
    linkReceipt,
    unlinkReceipt,
    mergeReceipts,
  },
})
